import { QueryResult, ResultStatus } from '@dispatch/shared/types';
import {
  QueryFunction,
  QueryKey,
  QueryStatus,
  useQuery as useReactQuery,
  UseQueryOptions,
} from 'react-query';

/**
 * Wraps react-query and maps to union status value
 * @param key
 * @param action
 * @returns { @see QueryResult }
 */
export function useQuery<
  TQueryFnData = unknown,
  TError = unknown,
  TData = TQueryFnData,
  TQueryKey extends QueryKey = QueryKey
>(
  queryKey: TQueryKey,
  queryFn: QueryFunction<TQueryFnData, TQueryKey>,
  options?: Omit<
    UseQueryOptions<TQueryFnData, TError, TData, TQueryKey>,
    'queryKey' | 'queryFn'
  >
): QueryResult<TData, TError> {
  const { data, error, refetch, ...rest } = useReactQuery(
    queryKey,
    queryFn,
    options
  );

  return {
    data,
    error,
    refetch,
    isFetching: rest.isFetching,
    status: mapQueryToStatus(rest, data),
  };
}

export function mapQueryStatus<TData>(
  data: TData,
  status: QueryStatus,
  hasFilters: boolean,
  isFetchingNextPage: boolean,
  isFetchingPreviousPage: boolean
): ResultStatus {
  if (
    isFetchingNextPage ||
    isFetchingPreviousPage ||
    status === 'idle' ||
    status === 'loading'
  ) {
    return 'loading';
  }

  if (status === 'success') {
    if (Array.isArray(data) && data.length <= 0 && !hasFilters) {
      return 'empty';
    } else if (Array.isArray(data) && data.length <= 0 && hasFilters) {
      return 'noresult';
    }
    return 'success';
  }

  return 'error';
}

export function mapQueryToStatus<TData>(
  result: { isLoading: boolean; isError: boolean; isSuccess: boolean },
  data: TData
): ResultStatus {
  if (result.isLoading) {
    return 'loading';
  }

  if (result.isSuccess) {
    if (Array.isArray(data) && data.length <= 0) {
      return 'empty';
    }
    return 'success';
  }

  return 'error';
}
